// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.textInput) return;

    this.hasTrackedPersonalizationStart = false;
    this.shouldShowValidation = false;
    this.#prefillFromQueryString();
    this.#updateCharacterCount();
    this.#validateAndSync({ showValidationMessage: false });
    this.#syncTitlePreview();
  }

  handleFocus() {
    if (this.hasTrackedPersonalizationStart) return;

    const { textInput } = this.refs;
    if (!textInput) return;

    this.hasTrackedPersonalizationStart = true;
    document.dispatchEvent(
      new CustomEvent('murmle:personalization-started', {
        detail: {
          productHandle: textInput.dataset.productHandle,
          mode: textInput.dataset.productMode,
          inputType: textInput.tagName.toLowerCase(),
        },
      })
    );
  }

  handleInput() {
    this.#updateCharacterCount();
    const { textInput } = this.refs;
    const hasValue = Boolean(textInput?.value.trim().length);
    this.#validateAndSync({ showValidationMessage: this.shouldShowValidation || hasValue });
    this.#syncTitlePreview();
  }

  handleBlur() {
    this.shouldShowValidation = true;
    this.#validateAndSync({ showValidationMessage: true });
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;

    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }

  #prefillFromQueryString() {
    const { textInput } = this.refs;
    const queryParam = textInput?.dataset.prefillQueryParam;

    if (!textInput || !queryParam || textInput.value) return;

    const params = new URLSearchParams(window.location.search);
    const prefilledValue = params.get(queryParam);

    if (!prefilledValue) return;

    textInput.value = prefilledValue.trim().slice(0, textInput.maxLength);
  }

  #validateAndSync({ showValidationMessage = true } = {}) {
    const { textInput, validationMessage } = this.refs;

    if (!textInput) return;

    const value = textInput.value.trim();
    const validationMode = textInput.dataset.validationMode || 'none';
    const validationError =
      textInput.dataset.validationError || 'Use 1 to 25 letters, spaces, apostrophes, or hyphens only.';
    const requiredMessage = textInput.dataset.requiredMessage || 'This field is required.';

    let validationMessageText = '';

    if (textInput.required && value.length === 0) {
      validationMessageText = requiredMessage;
    } else if (validationMode === 'child_name' && value.length > 0) {
      const childNamePattern = /^(?=.{1,25}$)[\p{L}\p{M}]+(?:[ '-][\p{L}\p{M}]+)*$/u;

      if (!childNamePattern.test(value)) {
        validationMessageText = validationError;
      }
    }

    textInput.setCustomValidity(validationMessageText);
    textInput.setAttribute('aria-invalid', validationMessageText ? 'true' : 'false');

    if (validationMessage) {
      validationMessage.textContent = validationMessageText;
      validationMessage.hidden = validationMessageText.length === 0 || !showValidationMessage;
    }

    this.#syncAddToCartState();
  }

  #syncAddToCartState() {
    const { textInput } = this.refs;
    const form = textInput?.form;

    if (!form) return;

    const addToCartButton = form.querySelector('[ref="addToCartButton"]');
    if (!addToCartButton) return;

    if (!('murmleServerDisabled' in addToCartButton.dataset)) {
      addToCartButton.dataset.murmleServerDisabled = addToCartButton.disabled ? 'true' : 'false';
    }

    const gatedInputs = Array.from(form.querySelectorAll('[data-validation-gates-cart="true"]'));
    const hasInvalidInput = gatedInputs.some((input) => !input.checkValidity());
    const serverDisabled = addToCartButton.dataset.murmleServerDisabled === 'true';

    addToCartButton.disabled = hasInvalidInput || serverDisabled;
  }

  #syncTitlePreview() {
    const { textInput } = this.refs;
    if (!textInput) return;

    const currentValue = textInput.value.trim();
    const previewNodes = document.querySelectorAll('[data-murmle-preview-title]');

    previewNodes.forEach((node) => {
      const titleTemplate = node.dataset.titleTemplate || '';
      const defaultChildName = node.dataset.defaultChildName || 'Maya';
      const baseTitle = node.dataset.baseTitle || titleTemplate || 'Your personalised story';
      const childName = currentValue || defaultChildName;

      node.textContent = titleTemplate
        ? this.#renderPersonalizedTitle(titleTemplate, childName)
        : baseTitle;
    });

    document.dispatchEvent(
      new CustomEvent('murmle:child-name-change', {
        detail: {
          hasValue: currentValue.length > 0,
          nameLength: currentValue.length,
          isValid: textInput.checkValidity(),
        },
      })
    );
  }

  #renderPersonalizedTitle(template, childName) {
    const possessiveChildName = /s$/i.test(childName) ? `${childName}'` : `${childName}'s`;

    return template
      .replaceAll('[child_name_possessive]', possessiveChildName)
      .replaceAll('[child_name]', childName);
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
